'use client';

/**
 * Meeting Card Component
 * Displays a meeting summary
 */

import { MeetingWithMember, MEETING_TYPE_CONFIG } from '@/lib/one-on-one/types';

interface MeetingCardProps {
  meeting: MeetingWithMember;
  onClick: () => void;
  showTeamMember?: boolean;
}

export function MeetingCard({
  meeting,
  onClick,
  showTeamMember = true,
}: MeetingCardProps) {
  const typeConfig = MEETING_TYPE_CONFIG[meeting.meeting_type];

  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isUpcoming = meeting.scheduled_at && !meeting.completed_at &&
    new Date(meeting.scheduled_at) > new Date();

  const isOverdue = meeting.scheduled_at && !meeting.completed_at &&
    new Date(meeting.scheduled_at) < new Date();

  return (
    <div
      onClick={onClick}
      className={`
        border rounded-lg p-4 cursor-pointer transition-colors
        ${meeting.completed_at
          ? 'border-border-muted hover:border-border/30'
          : isOverdue
            ? 'border-red-500/30 hover:border-red-500/50 bg-red-500/5'
            : 'border-border hover:border-border-muted0 bg-accent-muted'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Type icon */}
        <div className="text-2xl">{typeConfig.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{typeConfig.label}</span>
            {isUpcoming && (
              <span className="text-xs px-2 py-0.5 bg-accent-muted text-accent rounded">
                Upcoming
              </span>
            )}
            {isOverdue && (
              <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">
                Overdue
              </span>
            )}
            {meeting.completed_at && (
              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                Completed
              </span>
            )}
          </div>

          {showTeamMember && meeting.team_member && (
            <p className="text-sm text-foreground-secondary mt-1">
              with {meeting.team_member.name}
            </p>
          )}

          <div className="flex items-center gap-4 mt-2 text-xs text-foreground-tertiary">
            {meeting.scheduled_at && (
              <span>{formatDate(meeting.scheduled_at)}</span>
            )}
            {meeting.duration_minutes && (
              <span>{meeting.duration_minutes} min</span>
            )}
            {meeting.mood_rating && (
              <span>
                {'😊😐😕'.charAt(Math.min(2, 3 - Math.ceil(meeting.mood_rating / 2)))} {meeting.mood_rating}/5
              </span>
            )}
          </div>

          {meeting.summary && (
            <p className="text-sm text-foreground-tertiary mt-2 line-clamp-2">
              {meeting.summary}
            </p>
          )}
        </div>

        {/* Arrow */}
        <svg className="w-5 h-5 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

export default MeetingCard;
